/* eslint-disable react/prop-types */
import React from "react";
import { useTranslation } from "react-i18next";
import cx from "classnames";
import { FormControl, Tooltip } from "@material-ui/core";
import ReactSelect from "react-select";
import { ThreeDots } from "react-loader-spinner";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TextEditor from "../../../components/TextEditor/TextEditor";
import { formatFileTypeLabel } from "./utils/fileTypes";
import {
  sortDependenciesObject,
  getResponsible,
} from "./utils/inputDrawerUtils";
import {
  grayColor,
  whiteColor,
} from "../../../assets/jss/material-dashboard-pro-react";

export default function InputDrawerDetailsPanel({
  input,
  classes,
  userDetails,
  selectStyles,
  usersList,
  inputsStatus,
  drawerUi,
  drawerActions,
}) {
  const {
    inputDrawerShowExpectedFileContent,
    isSavingInputResponsible,
    isSavingInputDescription,
  } = drawerUi;
  const {
    onShowExpectedFileContentClick,
    onResponsibleChange,
    onSaveResponsible,
    onInputDetailsContentChange,
    onSaveInputDetails,
    onConnectionClick,
  } = drawerActions;

  const { t, i18n } = useTranslation();

  const renderInputConnections = (connection_ids) => {
    const connections = [];
    inputsStatus.forEach((input_group) => {
      input_group.inputs.forEach((inp) => {
        if (connection_ids.includes(inp.input_id)) {
          connections.push(inp);
        }
      });
    });
    return (
      <>
        {connections.map((conn, key) => {
          return (
            <Tooltip
              title={t("modeling_inputs_v2.click_to_open")}
              enterDelay={500}
              enterNextDelay={500}
              key={key}
            >
              <span
                className={cx({
                  [classes.inputDrawerTag]: true,
                  ["ready"]:
                    conn.stages.data_source === "success" &&
                    ["success", "warning"].includes(
                      conn.stages.data_structure,
                    ) &&
                    ["waiting", "success", "warning"].includes(
                      conn.stages.cross_linking,
                    ),
                })}
                onClick={() => onConnectionClick(conn)}
              >
                {conn.input_name[i18n.language]}
              </span>
            </Tooltip>
          );
        })}
      </>
    );
  };

  const renderInputDependencies = (dependencies) => {
    return (
      <>
        {sortDependenciesObject(dependencies).map((dep, key) => {
          return (
            <span
              key={key}
              className={cx({
                [classes.inputDrawerTag]: true,
                ["no-hover"]: true,
              })}
            >
              {dep["label"][i18n.language]}
              {" — "}
              {dep["data"].map((data, key2) => {
                return (
                  <span key={key2}>
                    <span style={{ color: data["ready"] ? "green" : "red" }}>
                      {t("months." + data["month"]) +
                        "/" +
                        (data["year"] % 100)}
                    </span>
                    {key2 < dep["data"].length - 1 && (
                      <span style={{ color: grayColor[3], margin: "0 3px" }}>
                        {" | "}
                      </span>
                    )}
                  </span>
                );
              })}
            </span>
          );
        })}
      </>
    );
  };

  if (!input) return null;

  return (
    <div className={classes.inputDetailsRoot}>
      <div className={classes.inputDetailsTopic}>
        <span className={classes.inputDetailsTopicName}>
          {t("modeling_inputs_v2.source.source") + ": "}
        </span>
        <span className={classes.inputDetailsTopicContent}>
          {t("modeling_inputs_v2.source." + input.source)}
        </span>
      </div>
      <div className={classes.inputDetailsTopic}>
        <span className={classes.inputDetailsTopicName}>
          {t("modeling_inputs_v2.frequency.frequency") + ": "}
        </span>
        <span className={classes.inputDetailsTopicContent}>
          {t("modeling_inputs_v2.frequency." + input.frequency)}
        </span>
      </div>
      <div className={classes.inputDetailsTopic}>
        <span className={classes.inputDetailsTopicName}>
          {t("modeling_inputs_v2.file_type") + ": "}
        </span>
        <span className={classes.inputDetailsTopicContent}>
          {formatFileTypeLabel(input.expected_file_type)}
        </span>
      </div>
      {["file_upload"].includes(input.source) && (
        <div className={classes.inputDetailsTopic}>
          <span className={classes.inputDetailsTopicName}>
            {t("modeling_inputs_v2.responsible") + ": "}
          </span>
          <span className={classes.inputDetailsTopicContent}>
            {!["DEV", "IMG", "CAD"].includes(userDetails.user_role) &&
              input.responsible.length > 0 && (
                <div className={classes.inputDetailsTagRow}>
                  {getResponsible(usersList, input.responsible, classes)}
                </div>
              )}
            {!["DEV", "IMG", "CAD"].includes(userDetails.user_role) &&
              input.responsible.length === 0 && (
                <div className={classes.inputDetailsTagRow}>
                  <span
                    className={cx({
                      [classes.inputDrawerTag]: true,
                      ["no-hover"]: true,
                      ["muted"]: true,
                    })}
                  >
                    {t("modeling_inputs_v2.no_responsible_defined")}
                  </span>
                </div>
              )}
            {["DEV", "IMG", "CAD"].includes(userDetails.user_role) && (
              <>
                <FormControl fullWidth className={classes.selectFormControl}>
                  <ReactSelect
                    isMulti
                    isClearable={false}
                    closeMenuOnSelect={false}
                    options={usersList.map((user) => ({
                      value: user.id,
                      label: user.name,
                    }))}
                    value={input.responsible.map((user) => ({
                      value: user.id,
                      label: user.name,
                    }))}
                    onChange={(e) => onResponsibleChange(e)}
                    placeholder={t("select") + "..."}
                    styles={{
                      ...selectStyles,
                    }}
                  />
                </FormControl>
                <div className={classes.inputDetailsSaveRow}>
                  <div
                    className={cx({
                      [classes.inputDetailsSaveButton]: true,
                      ["saving"]: isSavingInputResponsible,
                    })}
                    onClick={() => onSaveResponsible()}
                  >
                    {!isSavingInputResponsible && (
                      <span>{t("modeling_inputs_v2.save")}</span>
                    )}
                    {isSavingInputResponsible && (
                      <ThreeDots color={whiteColor} height={10} />
                    )}
                  </div>
                </div>
              </>
            )}
          </span>
        </div>
      )}
      <div className={classes.inputDetailsTopic}>
        <span className={classes.inputDetailsTopicName}>
          {t("modeling_inputs_v2.expected_content") + ": "}
        </span>
        <span
          className={cx({
            [classes.inputDetailsTopicContent]: true,
            [classes.inputDetailsTopicFunctionIcon]: true,
          })}
          onClick={onShowExpectedFileContentClick}
        >
          {inputDrawerShowExpectedFileContent ? (
            <FontAwesomeIcon icon={faEye} />
          ) : (
            <FontAwesomeIcon icon={faEyeSlash} />
          )}
        </span>
      </div>
      <div
        className={cx(
          classes.inputDetailsEditorWrap,
          inputDrawerShowExpectedFileContent
            ? classes.inputDetailsEditorWrapVisible
            : classes.inputDetailsEditorWrapHidden,
        )}
      >
        <TextEditor
          initialContent={input.input_details}
          setContent={onInputDetailsContentChange}
          readOnly={!["DEV", "IMG"].includes(userDetails.user_role)}
        />
        {["DEV", "IMG"].includes(userDetails.user_role) && (
          <div
            className={cx({
              [classes.inputDetailsSaveButton]: true,
              ["saving"]: isSavingInputDescription,
            })}
            onClick={() => onSaveInputDetails()}
          >
            {!isSavingInputDescription && (
              <span>{t("modeling_inputs_v2.save")}</span>
            )}
            {isSavingInputDescription && (
              <ThreeDots color={whiteColor} height={10} />
            )}
          </div>
        )}
      </div>
      <div className={classes.inputDetailsTopic}>
        <span className={classes.inputDetailsTopicName}>
          {t("modeling_inputs_v2.connections") + ": "}
        </span>
        {input.connections.length === 0 && (
          <span className={classes.inputDetailsTopicContent}>
            <em>{t("modeling_inputs_v2.no_connections")}</em>
          </span>
        )}
      </div>
      {input.connections.length > 0 && (
        <div className={classes.inputDetailsConnectionsRow}>
          {renderInputConnections(input.connections)}
        </div>
      )}
      {input.source === "auto_generated" && (
        <>
          <div className={classes.inputDetailsTopic}>
            <span className={classes.inputDetailsTopicName}>
              {t("modeling_inputs_v2.dependencies") + ": "}
            </span>
          </div>
          <div className={classes.inputDetailsConnectionsRow}>
            {renderInputDependencies(input.dependencies)}
          </div>
        </>
      )}
    </div>
  );
}
